import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { PageTitle, AdminButton, useList, saveDoc } from "@/components/admin/kit";
import { useUnsaved, UnsavedBadge, same } from "@/lib/unsaved";

const newOpt = () => ({ id: crypto.randomUUID(), name: "", swatch: "#e2e8f0", image_url: "", is_upgrade: false, price: 0 });

function Category({ cat, onSaved }) {
  const [c, setC] = useState(cat);
  useEffect(() => { setC(cat); }, [cat]);
  const dirty = !same(c, cat);
  useUnsaved(dirty);
  const upd = (i, patch) => setC({ ...c, options: c.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const save = async () => { if (await saveDoc("/admin/finish-categories", c)) onSaved(); };
  const del = async () => { if (window.confirm(`Delete ${c.name}?`)) { await api.delete(`/admin/finish-categories/${c.id}`); onSaved(); } };
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid={`admin-finish-category-${c.id || "new"}`}>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <Input className="max-w-xs font-serif text-lg" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} placeholder="Category name" data-testid="finish-cat-name" />
        <Input className="w-20" type="number" value={c.order} onChange={(e) => setC({ ...c, order: +e.target.value })} title="Order" data-testid="finish-cat-order" />
        <div className="flex-1" />
        <UnsavedBadge show={dirty} />
        {c.id && <AdminButton variant="danger" onClick={del} data-testid="finish-cat-delete"><Trash2 className="w-4 h-4" />Delete</AdminButton>}
        <AdminButton onClick={save} data-testid="finish-cat-save"><Save className="w-4 h-4" />Save</AdminButton>
      </div>
      <div className="space-y-3">
        {c.options.map((o, i) => (
          <div key={o.id} className="grid grid-cols-[auto_1fr] sm:grid-cols-[auto_auto_1fr_auto_120px_auto] gap-3 items-center border-t pt-3" data-testid={`finish-opt-row-${i}`}>
            <ImageUpload value={o.image_url} onChange={(v) => upd(i, { image_url: v })} testid={`finish-opt-image-${i}`} />
            <input type="color" value={o.swatch} onChange={(e) => upd(i, { swatch: e.target.value })} className="w-12 h-12 rounded-lg border cursor-pointer" data-testid={`finish-opt-swatch-${i}`} />
            <Input value={o.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Option name" data-testid={`finish-opt-name-${i}`} />
            <label className="flex items-center gap-2 text-sm"><Switch checked={o.is_upgrade} onCheckedChange={(v) => upd(i, { is_upgrade: v })} data-testid={`finish-opt-upgrade-${i}`} />Upgrade</label>
            <Input type="number" value={o.price} disabled={!o.is_upgrade} onChange={(e) => upd(i, { price: +e.target.value })} placeholder="$" data-testid={`finish-opt-price-${i}`} />
            <button onClick={() => setC({ ...c, options: c.options.filter((_, j) => j !== i) })} className="p-2 text-slate-400 hover:text-rose-600" data-testid={`finish-opt-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={() => setC({ ...c, options: [...c.options, newOpt()] })} className="mt-4 text-sm font-semibold text-bp inline-flex items-center gap-1" data-testid="finish-add-option"><Plus className="w-4 h-4" />Add option</button>
    </div>
  );
}

export default function AdminFinishes() {
  const [cats, reload] = useList("/admin/finish-categories");
  const [adding, setAdding] = useState(false);
  if (!cats) return null;
  return (
    <div>
      <PageTitle help="finishes" title="Finishes & upgrades" sub="Standard options are included; upgrades add to the buyer's running total" right={<AdminButton onClick={() => setAdding(true)} data-testid="finish-new-category"><Plus className="w-4 h-4" />New category</AdminButton>} />
      <div className="space-y-5">
        {adding && <Category cat={{ name: "", order: cats.length, options: [newOpt()] }} onSaved={() => { setAdding(false); reload(); }} />}
        {cats.map((c) => <Category key={c.id} cat={c} onSaved={reload} />)}
      </div>
    </div>
  );
}
